import { useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { MapPin, ListChecks, KeyRound, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';

const tabs = [
  {
    to: '/visits',
    icon: MapPin,
    label: 'Визиты',
  },
  {
    to: '/tasks',
    icon: ListChecks,
    label: 'Задачи',
  },
  {
    to: '/vault',
    icon: KeyRound,
    label: 'Пароли',
  },
  {
    to: '/more',
    icon: Menu,
    label: 'Ещё',
  },
];

interface TabBarProps {
  onTasksLongPress?: () => void;
  onMoreLongPress?: () => void;
}

export function TabBar({ onTasksLongPress, onMoreLongPress }: TabBarProps) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  const clearTimer = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const getLongPressProps = useCallback(
    (callback?: () => void) => {
      if (!callback) return {};

      return {
        onTouchStart: () => {
          longPressFired.current = false;
          longPressTimer.current = setTimeout(() => {
            longPressFired.current = true;
            navigator.vibrate?.(30);
            callback();
          }, 200);
        },
        onTouchEnd: (e: React.TouchEvent) => {
          clearTimer();
          if (longPressFired.current) {
            e.preventDefault();
          }
        },
        onTouchMove: clearTimer,
        onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
        onClick: (e: React.MouseEvent) => {
          if (longPressFired.current) {
            e.preventDefault();
            longPressFired.current = false;
          }
        },
      };
    },
    [clearTimer],
  );

  const longPressMap: Record<string, (() => void) | undefined> = {
    '/tasks': onTasksLongPress,
    '/more': onMoreLongPress,
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border h-[var(--tab-bar-height)] safe-area-bottom">
      <div className="flex h-full">
        {tabs.map((tab) => {
          const longPressProps = getLongPressProps(longPressMap[tab.to]);
          // Separate onClick from the rest — NavLink needs its own onClick handling
          const { onClick: longPressClick, ...touchProps } = longPressProps as any;

          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }: { isActive: boolean }) =>
                cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )
              }
              {...touchProps}
              onClick={longPressClick}
            >
              {({ isActive }: { isActive: boolean }) => (
                <>
                  <tab.icon
                    className={cn('h-5 w-5', isActive && 'stroke-[2.5]')}
                  />
                  <span>{tab.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
